import { defineComponent as _defineComponent } from 'vue';
import { computed, onBeforeUnmount, ref, useAttrs, useSlots, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button } from 'Components/primitives/button';
import { cn } from 'Components/primitives/utils';
import EnTooltip from '../EnTooltip/EnTooltip.vue';
import EnIcon from 'Components/icons/EnIcon.vue';
import { ButtonState } from 'Components/types/ButtonState';
import { EnDataGridTooltipLocation } from 'Components/types/EnDataGridTooltipLocation';
import { Icons16x16 } from 'Js/enums/icons';
import { buildStaticId } from 'Components/utils/buildStaticId';
export default /*@__PURE__*/ _defineComponent({
    ...{
        inheritAttrs: false,
    },
    __name: 'EnButton',
    props: {
        variant: { type: null, required: false, default: 'outline' },
        size: { type: null, required: false, default: 'default' },
        small: { type: Boolean, required: false, default: false },
        icon: { type: [String, null], required: false, default: null },
        prependIcon: { type: [String, null], required: false, default: null },
        appendIcon: { type: [String, null], required: false, default: null },
        nativeType: { type: String, required: false, default: 'button' },
        colorScheme: { type: [Object, null], required: false, default: null },
        width: { type: [String, Number, null], required: false, default: null },
        height: { type: [String, Number, null], required: false, default: null },
        noShadow: { type: Boolean, required: false, default: false },
        staticId: { type: [String, Number, null], required: false, default: null },
        disabled: { type: Boolean, required: false, default: false },
        state: { type: String, required: false, default: ButtonState.NORMAL },
        feedbackIconOnly: { type: Boolean, required: false, default: false },
        rounded: { type: Boolean, required: false, default: false },
        tooltip: { type: [String, null], required: false, default: null },
        tooltipLocation: { type: String, required: false, default: EnDataGridTooltipLocation.BOTTOM }
    },
    emits: ['click'],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const attrs = useAttrs();
        const slots = useSlots();
        const { t } = useI18n();
        const attrsRecord = computed(() => attrs);
        const isLoading = computed(() => props.state === ButtonState.LOADING);
        const isSuccess = computed(() => props.state === ButtonState.SUCCESS);
        const isError = computed(() => props.state === ButtonState.ERROR);
        const showFeedback = ref(false);
        let feedbackTimeoutId = null;
        const iconOnlyMode = computed(() => !!slots.icon || !!props.icon);
        const legacySkinClasses = new Set(['en-button', 'en-text-button', 'en-outline-button']);
        function collectClasses(value, out) {
            if (!value) {
                return;
            }
            if (typeof value === 'string') {
                out.push(...value.split(/\s+/).filter(Boolean));
                return;
            }
            if (Array.isArray(value)) {
                for (const item of value) {
                    collectClasses(item, out);
                }
                return;
            }
            if (typeof value === 'object') {
                for (const [key, enabled] of Object.entries(value)) {
                    if (enabled) {
                        out.push(key);
                    }
                }
            }
        }
        const externalClass = computed(() => {
            const classes = [];
            collectClasses(attrs.class, classes);
            return classes.filter((cls) => !legacySkinClasses.has(cls));
        });
        const forwardedAttrs = computed(() => {
            const rest = { ...attrs };
            delete rest.class;
            if (iconOnlyMode.value &&
                props.tooltip &&
                rest['aria-label'] == null &&
                rest['aria-labelledby'] == null &&
                rest.title == null) {
                rest['aria-label'] = props.tooltip;
            }
            return rest;
        });
        const tooltipStaticId = computed(() => {
            return props.tooltip ? `${buildId()}-tooltip` : undefined;
        });
        const resolvedVariant = computed(() => {
            switch (props.variant) {
                case 'primary':
                case 'filled':
                case 'flat':
                    return 'default';
                case 'error':
                    return 'destructive';
                case 'normal':
                    return 'secondary';
                case 'text':
                    return 'ghost';
                default:
                    return props.variant ?? 'secondary';
            }
        });
        const resolvedSize = computed(() => {
            if (props.small && props.size === 'default') {
                return iconOnlyMode.value ? 'icon-sm' : 'xs';
            }
            if (props.size) {
                return props.size;
            }
            if (props.small) {
                return iconOnlyMode.value ? 'icon-sm' : 'xs';
            }
            if (iconOnlyMode.value) {
                return 'icon';
            }
            return 'default';
        });
        const feedbackIcon = computed(() => {
            switch (props.state) {
                case ButtonState.SUCCESS:
                    return Icons16x16.check;
                case ButtonState.ERROR:
                    return Icons16x16.close;
                default:
                    return null;
            }
        });
        const feedbackMessage = computed(() => {
            switch (props.state) {
                case ButtonState.SUCCESS:
                    return t('common.buttons.feedback.success');
                case ButtonState.ERROR:
                    return t('common.buttons.feedback.error');
                default:
                    return null;
            }
        });
        const showFeedbackText = computed(() => !props.feedbackIconOnly && !!feedbackMessage.value);
        const hasCustomFeedbackSlot = computed(() => (isSuccess.value && !!slots.success) || (isError.value && !!slots.error));
        watch(isLoading, (now, before) => {
            if (!now && before && props.state !== ButtonState.NORMAL) {
                showFeedback.value = true;
                if (feedbackTimeoutId) {
                    clearTimeout(feedbackTimeoutId);
                }
                feedbackTimeoutId = setTimeout(() => {
                    showFeedback.value = false;
                    feedbackTimeoutId = null;
                }, 5000);
            }
        });
        onBeforeUnmount(() => {
            if (feedbackTimeoutId) {
                clearTimeout(feedbackTimeoutId);
                feedbackTimeoutId = null;
            }
        });
        const disabledComputed = computed(() => {
            const attrDisabled = attrsRecord.value?.disabled === '' || attrsRecord.value?.disabled === true || attrsRecord.value?.disabled === 'true';
            return isLoading.value || props.disabled || attrDisabled;
        });
        const buttonClass = computed(() => {
            return cn('en-base-button relative', externalClass.value, {
                'shadow-none': props.noShadow,
                'rounded-full': props.rounded,
                'en-custom-color': !!props.colorScheme,
                'icon-only': iconOnlyMode.value,
                'is-loading': isLoading.value,
                'is-feedback': showFeedback.value,
            });
        });
        const buttonStyle = computed(() => {
            const style = {};
            if (props.width != null) {
                style.width = typeof props.width === 'number' ? `${props.width}px` : props.width;
            }
            if (props.height != null) {
                style.height = typeof props.height === 'number' ? `${props.height}px` : props.height;
            }
            if (props.colorScheme?.background != null) {
                style.background = props.colorScheme.background;
            }
            if (props.colorScheme?.color != null) {
                style.color = props.colorScheme.color;
            }
            if (props.colorScheme?.border != null) {
                style.borderColor = props.colorScheme.border;
            }
            if (props.colorScheme?.borderRadius != null) {
                style.borderRadius = typeof props.colorScheme.borderRadius === 'number'
                    ? `${props.colorScheme.borderRadius}px`
                    : props.colorScheme.borderRadius;
            }
            return style;
        });
        const buildId = () => buildStaticId('en-base-action-button', props.staticId);
        function onClick(event) {
            if (disabledComputed.value) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }
            const href = typeof attrsRecord.value.href === 'string' ? attrsRecord.value.href : null;
            const target = typeof attrsRecord.value.target === 'string' ? attrsRecord.value.target : null;
            if (href) {
                const newTab = event.metaKey || event.ctrlKey || target === '_blank';
                if (newTab) {
                    window.open(href, '_blank', 'noopener,noreferrer');
                }
                else if (window.top && window.top !== window) {
                    window.top.location.href = href;
                }
                else {
                    window.location.href = href;
                }
                event.preventDefault();
                return;
            }
            emit('click', event);
        }
        __expose({
            buildId,
        });
        const __returned__ = { props, emit, attrs, slots, t, attrsRecord, isLoading, isSuccess, isError, showFeedback, get feedbackTimeoutId() { return feedbackTimeoutId; }, set feedbackTimeoutId(v) { feedbackTimeoutId = v; }, iconOnlyMode, legacySkinClasses, collectClasses, externalClass, forwardedAttrs, tooltipStaticId, resolvedVariant, resolvedSize, feedbackIcon, feedbackMessage, showFeedbackText, hasCustomFeedbackSlot, disabledComputed, buttonClass, buttonStyle, buildId, onClick, get Button() { return Button; }, EnTooltip, EnIcon };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
